import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import useAsyncEffect from 'use-async-effect';
import { IoMdMegaphone } from 'react-icons/io';
import { FiArrowLeft, FiMail, FiEdit2, FiUser, FiBell, FiCheck, FiSliders, FiAlertCircle, FiLoader, FiInfo, FiFileText, FiHash, FiImage, FiGlobe, FiType, FiAlignLeft } from 'react-icons/fi';
import { IGlobalState } from '../../../model/IGlobalState';
import { Template } from '../../../model/Template';
import { EmailMarketingSettings } from '../../../model/Stock';
import { TemplateSampleResult } from '../../../model/GetTemplatesResult';
import api from '../../../api/api';
import { to, plainText, fillTemplatePreview } from '../../../util/util';
import { ChooseAnnouncementTemplateModal, welcomeTemplateName, editPath, sendTemplateTest, welcomeMessageDetail } from './EmailTemplates';
import { SendTestModal } from './CampaignModals';

type View = 'Desktop' | 'Mobile' | 'Plain Text';

// placeholders the send fills, in the order the spec lists them; anything else in the HTML is fixed
const placeholders: { tag: string; icon: React.ReactNode; label: string; note: string; detail?: string }[] = [
    { tag: '{{announcement_headline}}', icon: <IoMdMegaphone />, label: 'Announcement title', note: 'fills itself' },
    { tag: '{{summary}}', icon: <FiInfo />, label: 'Plain-English summary', note: 'fills itself' },
    { tag: '{{announcement_url}}', icon: <FiFileText />, label: 'Link to the announcement PDF', note: 'fills itself' },
    { tag: '{{ticker}}', icon: <FiHash />, label: 'ASX code', note: 'fills itself' },
    { tag: '{{content_placeholder}}', icon: <FiAlignLeft />, label: 'Your message', note: 'you write it' },
    { tag: '{{welcome_message}}', icon: <FiAlignLeft />, label: 'Personal welcome message', note: 'you write it', detail: welcomeMessageDetail },
    { tag: '{{preview_line}}', icon: <FiType />, label: 'Preview line', note: 'you write it' },
    { tag: '{{logo}}', icon: <FiImage />, label: 'Company logo', note: 'fills itself' },
    { tag: '{{website_url}}', icon: <FiGlobe />, label: 'Website link', note: 'fills itself' },
    { tag: '{{list_unsubscribe}}', icon: <FiMail />, label: 'Unsubscribe footer', note: 'required' },
];

// spec 2:64826. Omitted: "Ready-made · used N times · last edited" (no data), Duplicate as a New Template (user decision).
const TemplatePreview: React.FC<{ templateId: string }> = ({ templateId }) => {
    const history = useHistory();
    const stock = useSelector((state: IGlobalState) => state.currentStock);
    const user = useSelector((state: IGlobalState) => state.user);
    const [testing, setTesting] = useState(false);
    const [all, setAll] = useState<Template[] | null>(null);
    const [settings, setSettings] = useState<EmailMarketingSettings | null>(null);
    const [sample, setSample] = useState<TemplateSampleResult | null>(null);
    const [error, setError] = useState('');
    const [view, setView] = useState<View>('Desktop');
    const [frameHeight, setFrameHeight] = useState(900);
    const [choosing, setChoosing] = useState(false);

    useAsyncEffect(async isMounted => {
        if (!stock?.id) return;
        setAll(null);
        setError('');
        const [err, data] = await to(Promise.all([api().getTemplates(stock.id), api().getEmailMarketingSettings(stock.id)]));
        if (!isMounted()) return;
        if (err) { setError(err.message || 'Failed to load the template'); return; }
        const [templatesResult, emailSettings] = data;
        setSettings(emailSettings);
        setAll(templatesResult.templates);
    }, [stock?.id]);

    const template = all?.find(t => t.id === templateId);
    const fillsAnnouncement = !!template && ['{{announcement_headline}}', '{{announcement_url}}', '{{summary}}'].some(x => template.html.includes(x));

    // may wait on the AI the first time (see TemplatesController.GetTemplateSampleResult)
    useAsyncEffect(async isMounted => {
        if (!stock?.id || !template) return;
        if (!fillsAnnouncement) { setSample({}); return; }
        setSample(null);
        const [, result] = await to(api().getTemplateSample(stock.id));
        if (isMounted()) setSample(result || {});
    }, [stock?.id, template?.id, fillsAnnouncement]);

    const back = <button type="button" className="link back" onClick={() => history.push('/communications/email-templates')}><FiArrowLeft /> Back to Email templates</button>;
    if (error) return <div className="announcement-preview template-preview">{back}<p className="error">{error}</p></div>;
    if (!all || !settings) return <div className="announcement-preview template-preview">{back}<p className="loading">Loading template…</p></div>;
    if (!template) return <div className="announcement-preview template-preview">{back}<p className="error">That template doesn't belong to {stock.companyName || stock.ticker}</p></div>;

    const isAnnouncement = template.id === settings.mailChimpTemplateId;
    const isWelcome = template.name === welcomeTemplateName;
    const html = sample === null ? null : fillTemplatePreview(template.html, stock.ticker, sample, sample.summary);
    const inside = placeholders.filter(p => template.html.includes(p.tag));

    const saveAnnouncementTemplate = async (id: string) => {
        const [err, result] = await to(api().saveTemplateSettings({ stockId: stock.id!, announcementTemplateId: id }));
        if (err || !result.success) {
            setError(err?.message || result?.errorMessage || 'Failed to save');
            return false;
        }
        setSettings({ ...settings, mailChimpTemplateId: id });
        return true;
    };

    const fitFrame = (e: React.SyntheticEvent<HTMLIFrameElement>) => {
        const body = e.currentTarget.contentDocument?.body;
        if (body) setFrameHeight(body.scrollHeight + 24);
    };

    const role = (icon: React.ReactNode, title: string, sub: string, used: boolean) =>
        <div className="row">
            {icon}
            <div className="t"><div className={used ? 'used' : ''}>{title}</div>{used && <div className="sub">{sub}</div>}</div>
            {used ? <FiCheck className="tick" /> : <span className="note">not used</span>}
        </div>;

    const stage = () => {
        if (html === null) return <p className="loading"><FiLoader className="spin" /> Building the email preview…</p>;
        return <>
            <div className="envelope">
                {settings.mailChimpReplyTo && <div className="from">From: {settings.mailChimpFromName || stock.companyName} &lt;{settings.mailChimpReplyTo}&gt;</div>}
                {sample?.headline && <div className="subject">Subject: {sample.headline}</div>}
            </div>
            {view === 'Plain Text'
                ? <pre className="plain">{plainText(html)}</pre>
                : <iframe title="Email preview" className={view === 'Mobile' ? 'mobile' : ''} srcDoc={html} sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                    style={{ height: frameHeight }} onLoad={fitFrame} />}
        </>;
    };

    return <div className="announcement-preview template-preview">
        {back}
        <div className="title-row">
            <div>
                <h1>{template.name}</h1>
                <div className="meta">
                    {isAnnouncement && <span className="pill info"><IoMdMegaphone /> Assigned · ASX announcements</span>}
                    {isWelcome && <span className="pill info"><FiUser /> Assigned · Welcome email</span>}
                    {!isAnnouncement && !isWelcome && <span className="pill">Not assigned</span>}
                </div>
            </div>
            <div className="actions">
                <button type="button" className="btn-outline" onClick={() => setTesting(true)}><FiMail /> Send Me a Test</button>
                <button type="button" className="btn-primary" onClick={() => history.push(editPath(template.id!))}><FiEdit2 /> Edit Template</button>
            </div>
        </div>

        {testing && <SendTestModal defaultTo={user.username} sub="This template, filled with your newest announcement" hideAddTest onClose={() => setTesting(false)} onSend={addresses => sendTemplateTest(stock, template.id!, addresses)} />}

        <div className="card views">
            {(['Desktop', 'Mobile', 'Plain Text'] as View[]).map(v =>
                <button key={v} type="button" className={v === view ? 'active' : ''} onClick={() => setView(v)}>{v}</button>)}
            {sample?.headline && <span className="hint">Filled with the last announcement so you can see how it really looks</span>}
        </div>

        <div className="body">
            <div className="stage">{stage()}</div>
            <div className="side">
                <div className="card">
                    <h2>Where it is used</h2>
                    {role(<IoMdMegaphone />, 'ASX announcements', 'every announcement email', isAnnouncement)}
                    {role(<FiUser />, 'Welcome email', 'sent when you invite a holder', isWelcome)}
                    {role(<FiBell />, 'Notifications', '', false)}
                    {!isWelcome && <button type="button" className="btn-outline sm" onClick={() => setChoosing(true)}><FiSliders /> Change What It Is Used For</button>}
                </div>
                <div className="card">
                    <h2>What is inside</h2>
                    {inside.length
                        ? inside.map(p => <div key={p.tag} className="row">{p.icon}<div className="t">{p.label}{p.detail && <div className="sub">{p.detail}</div>}</div><span className="note">{p.note}</span></div>)
                        : <p className="empty">Nothing fills itself — this template is fixed text.</p>}
                </div>
                <div className="card">
                    <button type="button" className="btn-primary" onClick={() => history.push(editPath(template.id!))}><FiEdit2 /> Edit Template</button>
                    <div className="warn-note"><FiAlertCircle /> Editing changes every future email that uses this template. Emails already sent stay as they were.</div>
                </div>
            </div>
        </div>

        {choosing && <ChooseAnnouncementTemplateModal templates={all.filter(t => t.name !== welcomeTemplateName)} currentId={settings.mailChimpTemplateId || ''}
            selectedId={template.id!} onClose={() => setChoosing(false)} onSave={saveAnnouncementTemplate} />}
    </div>;
};

export default TemplatePreview;
